import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';

export default function PatientAppInviteModal({ patient, clinicName, onHide }) {
  const [evidence, setEvidence] = useState('');
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const email = String(patient.email || '').trim();
  const birth = String(patient.data_nascimento || '').slice(0, 10);
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 18);
  const adultCutoff = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
  const eligible = /^\d{4}-\d{2}-\d{2}$/.test(birth) && birth <= adultCutoff && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const submit = async (event) => {
    event.preventDefault();
    if (!eligible || !verified || evidence.trim().length < 10 || pending.current) return;
    pending.current = true; setBusy(true); setError('');
    try {
      await axios.post('/api/patient-management/invites', { paciente_id: patient.id, evidence: evidence.trim() });
      setSent(true);
    } catch (err) {
      const messages = {
        403: 'Seu perfil não tem autorização para convidar pacientes nesta unidade.',
        404: 'O paciente não está disponível nesta unidade. Atualize a listagem.',
        409: 'Este paciente já possui um vínculo de acesso. Se estiver ativo, ele deve entrar no app ou usar “Esqueci minha senha”. Se foi revogado, revise o vínculo com o administrador.',
        422: 'Confira o e-mail, a data de nascimento do titular adulto e o registro de conferência no cadastro.',
        429: 'Limite de convites atingido. Aguarde alguns minutos antes de tentar novamente.',
        503: 'O envio não está configurado. Verifique a habilitação do portal, o endereço HTTPS do app e o serviço de e-mail no backend.',
      };
      setError(messages[err.response?.status] || 'Não foi possível confirmar o envio. Confira o e-mail do paciente antes de tentar novamente.');
    } finally { pending.current = false; setBusy(false); }
  };
  return <Modal show centered backdrop="static" onHide={busy ? undefined : onHide}>
    <Form onSubmit={submit}>
      <Modal.Header closeButton={!busy}><Modal.Title>Acesso ao App do paciente</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="rounded p-3 mb-3" style={{ background: '#eef7f7', border: '1px solid #d5e8e8' }}>
          <div className="fw-bold">{patient.nome}</div>
          {clinicName && <div className="text-muted small mb-2">{clinicName}</div>}
          <div className="small">E-mail de ativação</div>
          <div style={{ overflowWrap: 'anywhere' }}>{email || 'Não cadastrado'}</div>
          <div className="small text-muted mt-2">Nascimento: {/^\d{4}-\d{2}-\d{2}$/.test(birth) ? birth.split('-').reverse().join('/') : 'Não informado'}</div>
        </div>
        {sent ? <Alert variant="success" role="status">
          <strong>Convite encaminhado para envio.</strong>
          <p className="mb-0 mt-2">O paciente receberá um link no e-mail cadastrado para ativar a conta e definir sua senha. O convite vale por 24 horas. Se já possui conta em outra clínica, deverá entrar para aceitar o vínculo.</p>
          <p className="mb-0 mt-2 small">O processamento depende da fila de e-mails. Oriente o paciente a verificar também a pasta de spam.</p>
        </Alert> : <>
          {!eligible && <Alert variant="warning">O acesso atual é destinado ao titular com 18 anos ou mais. Antes de convidar, feche esta janela e confira o e-mail e a data de nascimento no cadastro do paciente.</Alert>}
          <p className="text-muted small">O convite será enviado somente ao e-mail cadastrado. Um novo envio invalida o convite anterior ainda não utilizado. A senha é definida pelo próprio paciente.</p>
          <Form.Group className="mb-3" controlId="patient-app-invite-evidence">
            <Form.Label>Registro da conferência de identidade e contato</Form.Label>
            <Form.Control as="textarea" rows={3} minLength={10} maxLength={500} required disabled={busy || !eligible} value={evidence} onChange={(event) => setEvidence(event.target.value)} placeholder="Descreva como a identidade e o e-mail do titular foram conferidos." />
            <Form.Text>De 10 a 500 caracteres. Não inclua senhas nem informações clínicas.</Form.Text>
          </Form.Group>
          <Form.Check id="patient-app-invite-verified" type="checkbox" required disabled={busy || !eligible} label="Conferi a identidade do titular e confirmei que este e-mail pertence a ele." checked={verified} onChange={(event) => setVerified(event.target.checked)} />
        </>}
        {error && <Alert variant="danger" role="alert" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" disabled={busy} onClick={onHide}>Fechar</Button>
        {!sent && <Button type="submit" disabled={busy || !eligible || !verified || evidence.trim().length < 10}>
          {busy && <Spinner size="sm" animation="border" className="me-2" />}
          {busy ? 'Encaminhando convite...' : 'Enviar convite por e-mail'}
        </Button>}
      </Modal.Footer>
    </Form>
  </Modal>;
}
